import { mdiChevronRight } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import { DateRange } from '@mui/x-date-pickers-pro'
import { BucketCard } from 'components/Buckets'
import { PPDrawer } from 'components/PPDrawer'
import PPSwitch from 'components/PPSwitch'
import { add, format } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { connectedDestinationsSelector } from 'services/destinations'
import { createBucket } from 'services/post/actions'
import { bucketGroupsSelector, bucketsSelector } from 'services/post/selectors'
import { checkFeatureAvailability, checkProductLimit } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_POST_SEASONAL_BUCKETS, LIMIT_POST_BUCKETS } from 'shared/constants'
import { Bucket } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { defaultBucketColor } from 'utils/colors'
import { BucketGroupSelector } from '../BucketGroupSelector'
import { BucketOptions } from '../BucketOptions'
import BucketProfilesPicker from '../BucketProfilesPicker'
import { EditableBucket } from '../EditableBucket'
import styles from './BucketCreatePopup.pcss'

interface BucketCreatePopupProps {
  open: boolean
  init?: {
    name: string
    description: string
    color: string
  }
  onCreated?: (bucket: Bucket) => void
  onClose: () => void
}

const newBucket: Omit<Bucket, 'pages' | 'postsCount'> = Object.freeze({
  id: 'new',
  name: '',
  description: '',
  color: defaultBucketColor,
  ppids: [],
  isPublic: false,
  isActive: false,
  order: 0,
  groupId: null
})

export function BucketCreatePopup(props: BucketCreatePopupProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const now = new Date()
   
  const [dateRange, setDateRange] = useState<DateRange<Date>>([now, add(now, { days: 30 })])
  const [withTimePeriod, setWithTimePeriod] = useState(false)
  const [loading, setLoading] = useState(false)
  const [bucket, setBucket] = useState<Omit<Bucket, 'pages' | 'postsCount'>>(newBucket)
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([])
  const [toggle, setToggle] = useState({ range: false, group: false })
  const profiles = useSelector(connectedDestinationsSelector)
  const buckets = useSelector(bucketsSelector)
  const groups = useSelector(bucketGroupsSelector)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)

  useEffect(() => {
    if (props.init) {
      setBucket(current => ({ ...current, ...props.init }))
    }
  }, [props.init])

  useEffect(() => {
    if (!props.open) {
      setBucket(newBucket)
      setSelectedProfileIds([])
    }
  }, [props.open])

  const onChange = useCallback((b: Partial<Bucket>) => {
    setBucket(current => ({ ...current, ...b }))
  }, [])

  const onCreate = () => {
    const name = bucket.name.trim()
    if (!name) {
      dispatch(message(intl.formatMessage({ id: 'post.buckets.form.error-name-empty' }), 'warning'))
      return
    }
    if (buckets.find(b => b.name.toLowerCase() === name.toLowerCase())) {
      dispatch(message(intl.formatMessage({ id: 'post.buckets.notification-bucket-exists' }), 'warning'))
      return
    }
    const isWithinLimit = dispatch(checkProductLimit(LIMIT_POST_BUCKETS, buckets.length + 1))
    if (!isWithinLimit) {
      return
    }
    const ppids = selectedProfileIds.map(id => profiles[id].ppid)
    let from: string | undefined = dateRange[0] ? format(dateRange[0], 'yyyy-LL-dd') : undefined
    let to: string | undefined = dateRange[1] ? format(dateRange[1], 'yyyy-LL-dd') : undefined
    if (!withTimePeriod) {
      from = undefined
      to = undefined
    }
    const description = bucket.description?.trim() || `Sharing posts with ${name}`

    let order
    const group = selectedGroupId && groups.find(g => g.id === selectedGroupId)
    if (group) {
      order = Math.max(...group.bucketIds.map(id => group.buckets.get(id)!.order), 0) + 1
    }
    setLoading(true)
    dispatch(createBucket({
      name,
      color: bucket.color,
      description,
      isPublic: Boolean(bucket.isPublic),
      ppids,
      activeFrom: from,
      activeTo: to,
      groupId: selectedGroupId,
      order
    }))
      .unwrap()
      .then(bucket => {
        dispatch(message(intl.formatMessage({ id: 'post.buckets.notification-bucket-created' }), 'success'))
        if (props.onCreated) {
          props.onCreated(bucket)
        }
      })
      .catch(() => {
        dispatch(message(intl.formatMessage({ id: 'post.notifications.error-generic' }), 'error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const toggleWithTimePeriod = (value: 'off' | 'on') => {
    if (value === 'on') {
      const featureAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_SEASONAL_BUCKETS))
      if (!featureAllowed) {
        return
      }
    }
    setWithTimePeriod(!withTimePeriod)
  }

  const toggleSection = (section: 'range' | 'group') => () => {
    setToggle(current => ({
      ...current,
      [section]: !current[section]
    }))
  }

  const footer = (
    <div className={styles['footer-actions']}>
      <Button variant="text" onClick={props.onClose}>
        <FormattedMessage id="actions.cancel" />
      </Button>
      <Button
        variant="contained"
        color="primary"
        className={styles['btn-save']}
        disabled={loading || selectedProfileIds.length === 0}
        onClick={onCreate}
      >
        <FormattedMessage id="actions.save-bucket" />
      </Button>
    </div>
  )

  return (
    <PPDrawer
      open={props.open}
      onClose={props.onClose}
      size="xl"
      disableEnforceFocus
      title={<FormattedMessage id="post.buckets.form.title-create" />}
      footer={footer}
    >
      <div className={styles.container}>
        <div className={styles.top}>
          <BucketCard bucket={bucket as any} noSelection className={styles['bucket-card']} />
          <div className={styles['section-title']}>
            <span className={styles.step}>1</span>
            <FormattedMessage id="post.buckets.form.title-name-color" />
          </div>
          <EditableBucket
            bucket={bucket}
            loading={loading}
            onChange={onChange}
          />
        </div>
        <div className={styles.bottom}>
          <div>
            <div className={styles['section-title']}>
              <span className={styles.step}>2</span>
              <FormattedMessage id="post.buckets.form.title-select-profiles" />
            </div>
            <div className={styles.subtitle}>
              <FormattedMessage id="post.buckets.form.subtitle-select-profiles" />
            </div>
          </div>
          <BucketProfilesPicker
            allSelectedInitial
            selectedProfileIds={selectedProfileIds}
            onSelectedProfilesChange={setSelectedProfileIds}
          />

          <Collapse in={toggle.range} collapsedSize={64}>
            <div className={`${styles.options} ${toggle.group ? styles.expanded : ''}`}>
              <div className={`${styles['section-title']} ${styles.clickable}`} onClick={toggleSection('range')}>
                <span className={styles.step}>3</span>
                <FormattedMessage id="post.buckets.form.title-time-period" />
                <Icon path={mdiChevronRight} size="24px" className={styles['icon-chevron']} />
              </div>
              <div style={{ marginLeft: '40px' }}>
                <PPSwitch
                  selectedValue={withTimePeriod ? 'on' : 'off'}
                  options={[{ value: 'off', label: 'Always' }, { value: 'on', label: 'Custom time period' }]}
                  className={styles.switch}
                  onSelectedValueChange={toggleWithTimePeriod}
                />
                <BucketOptions
                  dateRange={dateRange}
                  disabled={!withTimePeriod}
                  onChange={setDateRange}
                />
              </div>
            </div>
          </Collapse>
          <Collapse in={toggle.group} collapsedSize={64}>
            <div className={toggle.group ? styles.expanded : ''}>
              <div className={styles['options-title']}>
                <div className={`${styles['section-title']} ${styles.clickable}`} onClick={toggleSection('group')}>
                  <span className={styles.step}>4</span>
                  <FormattedMessage id="post.buckets.form.title-group" />
                  <Icon path={mdiChevronRight} size="24px" className={styles['icon-chevron']} />
                </div>
              </div>
              <div className={styles['options-box']}>
                <BucketGroupSelector selectedGroupId={selectedGroupId} onSelectionChange={setSelectedGroupId} />
              </div>
            </div>
          </Collapse>
        </div>
      </div>
    </PPDrawer>
  )
}

export default BucketCreatePopup
